import styles from "./styles.module.scss";
import { ReactNode, useState, useMemo } from "react";
import { uniqueId } from "lodash";
import { BetterPopover } from "../popover";
import classNames from "classnames";
import { Button, Stack } from "@altimateai/lego";
import { default as ArrowRightFullIcon } from "@/assets/icons/arrow-right-full.svg?react";
import { NativeSelect } from "@altimateai/lego";

interface FilterMode {
  value: string;
}

export interface FilterTextChangeValue {
  value: string;
  mode: string | undefined;
}

interface FilterTextProps {
  label: ReactNode;
  value?: string;
  children: ReactNode;
  onChange: (value: string | FilterTextChangeValue) => void;
  placeHolder?: string;
  filterMode?: FilterMode;
  allowFractional?: boolean;
}

export const FilterText = ({
  label,
  value,
  children,
  onChange,
  placeHolder,
  filterMode,
  allowFractional = false,
}: FilterTextProps) => {
  const id = useMemo(() => uniqueId("filter-text-"), []);
  const [defaultValue, changeDefaultValue] = useState(value || "");
  const numbersOnly = useMemo(
    () => (allowFractional ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]+$/),
    [allowFractional]
  );

  const isDisabled = defaultValue === "";
  const [filterModeValue, setFilterModeValue] = useState<string | undefined>(filterMode?.value);

  return (
    <BetterPopover
      id={`popover-${id}`}
      trigger={
        <Stack gap="sm" className="cursor-pointer">
          {children}
        </Stack>
      }
      render={({ close }) => (
        <div className={classNames(styles.dropdown_container, styles.async_container)}>
          <Stack direction="column" gap="xxs" className="m-2 al-justify-left al-items-left">
            <div className="fs-5 fw-semibold">{label}</div>
            {filterMode ? (
              <NativeSelect
                size={"sm"}
                value={filterModeValue}
                onChange={setFilterModeValue}
                options={[
                  { label: "greater than", value: "gt" },
                  { label: "less than", value: "lt" },
                ]}
                variant="outline"
                className="al-p-0 !al-w-full"
              />
            ) : (
              <div className="fs-6 fw-lighter">(greater than)</div>
            )}
          </Stack>
          <div className="divider al-w-full" />
          <div className="m-2">
            <div className={styles.user_input}>
              <input
                value={defaultValue}
                onChange={e => {
                  if (numbersOnly.test(e.target.value) || e.target.value === "") {
                    changeDefaultValue(e.target.value);
                  }
                }}
                placeholder={placeHolder}
                onKeyDown={e => {
                  if (e.key == "Enter") {
                    e.stopPropagation();
                    close();
                    if (filterMode) {
                      onChange({
                        value: defaultValue,
                        mode: filterModeValue,
                      });
                    } else {
                      onChange(defaultValue);
                    }
                  }
                }}
              />
              <Button
                size="sm"
                disabled={isDisabled}
                onClick={e => {
                  e.stopPropagation();
                  close();
                  if (filterMode) {
                    onChange({ value: defaultValue, mode: filterModeValue });
                  } else {
                    onChange(defaultValue);
                  }
                }}
              >
                <ArrowRightFullIcon />
              </Button>
            </div>
          </div>
        </div>
      )}
    />
  );
};
